import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
    ActionButton,
    Content,
    Dialog,
    DialogTrigger,
    Divider,
    Flex,
    Heading,
    Item,
    TabList,
    TabPanels,
} from '@adobe/react-spectrum';
import { DeepLinkTabs } from '../../utils/deepLink/DeepLinkTabs';
import Data from '@spectrum-icons/workflow/Data';
import ViewList from '@spectrum-icons/workflow/ViewList';
import { SORT_MAP } from '../../data/redux/slices/leaderboardSlice';
import './Commanders.css';
import { getGrades } from '../../data/ScoreDisplayUtils';
import { Helmet } from 'react-helmet';
import { ResultsListing } from '../resultsListing/ResultsListing';
import { resultsColumns } from '../resultsListing/columns/ResultsColumns';
import { resultsCellRenderers } from '../resultsListing/renderers/ResultsCellRenderers';
import { resultsColumnRenderers } from '../resultsListing/renderers/ResultsColumnRenderers';
import isMobile from 'ismobilejs';
import {
    commandersFetchFiltered,
    fetchCommanderDetailItems,
    setCommanderDetailsSortBy,
    setCommanderDetailsSortDirection,
    setSelectedListIndex,
} from '../../data/redux/slices/commanderDetailsSlice';
import { BreadCrumbs } from '../breadcrumbs/BreadCrumbs';
import { CommanderCardStatsPanel } from './CommanderCardStatsPanel';
import { truncateToOneDecimal } from '../common/cardStats/formatters';
import { capBracketRating, capPowerLevel } from '../../data/displayCaps';
import '../common/searedDialog/searedDialog.css';

const SCORING_STAGES = [
    {
        key: 'density',
        stage: 'Stage 1',
        name: 'Density weighting',
        tagline: 'Dense clusters win',
        body:
            "Each deck contributes proportionally to how dense its bucket is in the commander's overall distribution. " +
            'A commander with a clear cluster of high-power builds reads higher than the raw mean suggests, because ' +
            "bimodal populations don't collapse into the valley between their modes.",
    },
    {
        key: 'shrinkage',
        stage: 'Stage 2',
        name: 'Low-sample shrinkage',
        tagline: 'Small N pulled to center',
        body:
            'Commanders with few decks, or with wildly variable ratings, are pulled toward the midpoint of the scale. ' +
            "A five-deck commander can't inherit a 9.5 power level from a single outlier deck.",
    },
    {
        key: 'anchor',
        stage: 'Stage 3',
        name: 'Tournament tier anchor',
        tagline: 'cEDH tier ceiling',
        body:
            'Commanders in the cEDH meta feed blend their weighted sample with a tier-derived ceiling. Tier-1 commanders ' +
            'sit near the top of each scale; tier-4 commanders get a smaller bump. Untracked commanders skip this stage ' +
            'entirely.',
    },
    {
        key: 'synergy',
        stage: 'Exempt',
        name: 'Synergy',
        tagline: 'Plain arithmetic mean',
        body:
            "Synergy doesn't go through any of the stages above — it's the flat average of the per-deck synergy scores. " +
            'Synergy already measures card-overlap against the commander itself, so weighting by distribution density ' +
            "wouldn't mean anything meaningful.",
        exempt: true,
    },
];

function ScoringMethodDialog() {
    return (
        <Dialog
            size="L"
            padding={0}
            width="calc(100vw - 40px)"
            maxWidth={860}
            UNSAFE_className="searedDialog scoringDialog"
        >
            <Heading>
                <span className="searedDialog__eyebrow">Methodology</span>
                <span className="searedDialog__title">How commander scores are calculated</span>
                <span className="searedDialog__subtitle">Three of these four ratings aren&rsquo;t plain averages.</span>
            </Heading>
            <Divider />
            <Content UNSAFE_className="scoringDialog__content">
                <div className="searedDialog__body">
                    <p className="searedDialog__paragraph">
                        <strong>Power Level</strong>, <strong>Bracket</strong>, and <strong>Saltiness</strong> on this
                        page are aggregated across every deck submitted for this commander using a three-stage formula.
                        The goal is to reflect how the commander is actually being built in the wild — not a flat mean
                        that would settle in the valley between modes of a bimodal population.
                    </p>
                    <div className="scoringDialog__grid">
                        {SCORING_STAGES.map((stage) => (
                            <div
                                key={stage.key}
                                className={`scoringDialog__card${stage.exempt ? ' scoringDialog__card--exempt' : ''}`}
                            >
                                <div className="scoringDialog__cardHead">
                                    <span className="scoringDialog__cardName">{stage.name}</span>
                                    <span className="scoringDialog__cardKind">{stage.stage}</span>
                                </div>
                                <span className="scoringDialog__cardTagline">{stage.tagline}</span>
                                <p className="scoringDialog__cardBody">{stage.body}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </Content>
        </Dialog>
    );
}

function ScoringMethodExplainer() {
    return (
        <div className="scoringExplainer">
            <span className="scoringExplainer__copy">
                Power Level, Bracket, and Saltiness are weighted by this commander&rsquo;s deck distribution —
                tournament-proven commanders anchor to their cEDH tier, and low-sample populations drift toward the
                middle. Synergy is a plain average.
            </span>
            <DialogTrigger type="modal" isDismissable>
                <ActionButton UNSAFE_className="scoringExplainer__link" isQuiet>
                    Learn how this works →
                </ActionButton>
                {() => <ScoringMethodDialog />}
            </DialogTrigger>
        </div>
    );
}

function CommanderHeroImage({ uri, name }) {
    const [errored, setErrored] = useState(false);
    if (!uri || errored) {
        return (
            <div className="heroPanelImage heroPanelImageFallback" role="img" aria-label={name}>
                <span className="heroPanelImageFallbackLabel">Image unavailable</span>
                <span className="heroPanelImageFallbackName">{name}</span>
            </div>
        );
    }
    return <img src={uri} alt={name} className="heroPanelImage" onError={() => setErrored(true)} />;
}

export function CommanderDetails() {
    const dispatch = useDispatch();
    const listItems = useSelector((state) => state?.commanderDetails?.listItems);
    const nextCursor = useSelector((state) => state?.commanderDetails?.nextCursor);
    const isFetching = useSelector((state) => state?.commanderDetails?.isFetching);
    const loadingState = useSelector((state) => state?.commanderDetails?.loadingState);
    const searchFilters = useSelector((state) => state?.commanderDetails?.filters);
    const details = useSelector((state) => state.commanderDetails?.details);
    const sortBy = useSelector((state) => state?.commanderDetails?.sortBy);
    const selectedListKey = useSelector((state) => state.commanderDetails.selectedListKey);
    const selectedListIndex = useSelector((state) => state.commanderDetails.selectedListIndex);

    const previewIsShowing = useSelector((state) => state.preview.isShowing);
    // Mobile layout kicks in on phone UAs OR narrow viewports. Resolution
    // check catches desktop browsers resized narrow, and lets Playwright
    // viewport tests exercise the mobile branch without UA spoofing.
    const [mobile, setMobile] = useState(() => {
        if (typeof window === 'undefined') return false;
        if (isMobile(window.navigator).phone) return true;
        return window.matchMedia('(max-width: 768px)').matches;
    });
    useEffect(() => {
        if (typeof window === 'undefined') return undefined;
        const mql = window.matchMedia('(max-width: 768px)');
        const onChange = () => setMobile(isMobile(window.navigator).phone || mql.matches);
        mql.addEventListener('change', onChange);
        return () => mql.removeEventListener('change', onChange);
    }, []);

    const handleIndexSelection = (evn) => {
        dispatch(setSelectedListIndex(evn));
    };

    const handleSortChange = (sortIndex) => {
        dispatch(setCommanderDetailsSortBy(SORT_MAP[sortIndex], searchFilters));
        dispatch(fetchCommanderDetailItems(null, true, SORT_MAP[sortIndex], sortDirection, searchFilters));
    };

    const sortDirection = useSelector((state) => state.commanderDetails.sortDirection);
    const handleSortDirectionChange = (direction) => {
        dispatch(setCommanderDetailsSortDirection(direction));
        dispatch(fetchCommanderDetailItems(null, true, sortBy, direction, searchFilters));
    };

    const handleLoadMore = () => {
        if (!isFetching && nextCursor !== null) {
            dispatch(fetchCommanderDetailItems(nextCursor, false, sortBy, sortDirection, searchFilters));
        }
    };

    const handleFiltersChange = (filters) => {
        dispatch(commandersFetchFiltered(filters, sortBy, sortDirection));
    };

    let commanderNames = '';
    let commanderNamesOneLine = '';

    for (let i = 0; i < details?.commanders?.length; i++) {
        if (i > 0) {
            commanderNames = `${commanderNames}\n`;
            commanderNamesOneLine = `${commanderNamesOneLine} + `;
        }

        commanderNames = `${commanderNames}${details?.commanders?.[i]?.name}`;
        commanderNamesOneLine = `${commanderNamesOneLine}${details?.commanders?.[i]?.name}`;
    }

    const grades = details ? getGrades(details) : null;

    const leftPanelClassname = details?.commanders?.length > 1 ? 'leftPanelPartner' : 'leftPanelPartner';

    // const filteredListItems = (listItems || []).filter((item) => {
    //     return item !== null && item.hasOwnProperty('id');
    // });

    const getImageUri = (commander) => {
        if (commander?.metadata?.hasOwnProperty('imageUris')) {
            if (metadata?.image_uris?.border_crop) {
                return metadata?.image_uris?.border_crop;
            } else {
                return metadata?.card_faces?.[0]?.image_uris?.border_crop || '';
            }
        } else {
            return commander.faces[commander.frontFaceId].metadata.image_uris.border_crop;
        }
    };

    const helmetName = commanderNamesOneLine
        ? `Commanders - ${commanderNamesOneLine}`
        : `Commander Details - Loading...`;
    const helmetCanonical = `https://www.commandersalt.com/commanders/id${details?.id}`;
    const helmetDescription = `MtG Commander decks using ${commanderNamesOneLine}`;
    const helmetTitle = `Commandersalt.com - MtG Commander decks using ${commanderNamesOneLine}`;

    return (
        <Flex direction="column">
            <Helmet>
                <title>{helmetName}</title>
                <meta name="creator" content="Ben Rimbey" />
                <link rel="canonical" href={helmetCanonical} />
                <meta name="description" content={helmetDescription} />
                <meta name="twitter:title" content={helmetTitle} />
                <meta property="og:site_name" content="Commandersalt.com" />
                <meta property="og:title" content={helmetTitle} />
                <meta property="og:image" content="https://www.commandersalt.com/chef-kiss-meta-image.png" />
                <meta property="og:description" content={helmetDescription} />
            </Helmet>
            <BreadCrumbs />
            <div className="heroPanelWrap">
                {details ? (
                    <div className="heroPanelCard panelGradient">
                        <div className="heroPanelImages">
                            {details?.commanders?.map((commander) => (
                                <CommanderHeroImage
                                    key={`image_key__${commander.id}`}
                                    uri={getImageUri(commander)}
                                    name={commander.name}
                                />
                            ))}
                        </div>
                        <div className="heroPanelInfo">
                            <div className="heroPanelEyebrow">
                                {`Meta breakdown · ${details?.count ?? 0} deck${details?.count === 1 ? '' : 's'}`}
                            </div>
                            <div className="heroPanelName">{commanderNamesOneLine}</div>
                            <div className="heroPanelStatsGrid">
                                <div className="heroPanelStat">
                                    <div className="heroPanelStatLabel">Power Level</div>
                                    <div
                                        className="heroPanelStatValue"
                                        style={{ color: grades?.['powerlevel']?.color }}
                                    >
                                        {truncateToOneDecimal(capPowerLevel(details?.powerLevelRating))}
                                    </div>
                                </div>
                                <div className="heroPanelStat">
                                    <div className="heroPanelStatLabel">Bracket</div>
                                    <div className="heroPanelStatValue" style={{ color: grades?.['bracket']?.color }}>
                                        {truncateToOneDecimal(capBracketRating(details?.bracketRating))}
                                    </div>
                                </div>
                                <div className="heroPanelStat">
                                    <div className="heroPanelStatLabel">Saltiness</div>
                                    <div className="heroPanelStatValue" style={{ color: grades?.['salt']?.color }}>
                                        {truncateToOneDecimal(details?.saltRating)}
                                    </div>
                                </div>
                                <div className="heroPanelStat">
                                    <div className="heroPanelStatLabel">Synergy</div>
                                    <div className="heroPanelStatValue" style={{ color: grades?.['synergy']?.color }}>
                                        {truncateToOneDecimal(details?.synergyRating)}
                                    </div>
                                </div>
                            </div>
                            <ScoringMethodExplainer />
                        </div>
                    </div>
                ) : (
                    <div className="heroPanelCard panelGradient" aria-busy="true" aria-live="polite">
                        <div className="heroPanelImages">
                            <div className="heroPanelImage skeletonBlock" />
                        </div>
                        <div className="heroPanelInfo">
                            <div className="heroPanelEyebrow skeletonBlock" />
                            <div className="heroPanelName skeletonBlock" />
                            <div className="heroPanelStatsGrid">
                                {['Power Level', 'Bracket', 'Saltiness', 'Synergy'].map((label) => (
                                    <div className="heroPanelStat is-skeleton" key={label}>
                                        <div className="heroPanelStatLabel skeletonBlock" />
                                        <div className="heroPanelStatValue skeletonBlock" />
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </div>
            {(() => {
                const deckListColumn = (
                    <div
                        className={`commanderDetailsColumn${mobile ? '' : ' panelGradient'}`}
                        style={
                            mobile
                                ? { display: 'initial', width: '100%', minWidth: 0 }
                                : {
                                      display: 'initial',
                                      flex: '1 1 380px',
                                      width: 380,
                                      minWidth: 320,
                                      maxWidth: 500,
                                  }
                        }
                    >
                        <ResultsListing
                            includeSortByIngestDate={true}
                            filterContext="decks"
                            sortBy={sortBy}
                            sortDirection={sortDirection}
                            handleSortChange={handleSortChange}
                            handleSortDirectionChange={handleSortDirectionChange}
                            disableNavChanges={true}
                            allowDeckPreview={true}
                            handleSelectionChange={null}
                            handleIndexSelection={handleIndexSelection}
                            selectedListIndex={selectedListIndex}
                            selectedListIndexPrefix={'commanderDetails_'}
                            selectedListKey={selectedListKey}
                            hasMore={nextCursor !== null}
                            columns={resultsColumns.LEADERBOARD_COMPACT()}
                            columnRenderer={resultsColumnRenderers.LEADERBOARD}
                            listItems={listItems}
                            cellRenderer={resultsCellRenderers.LEADERBOARD}
                            previewIsShowing={previewIsShowing}
                            handleLoadMore={handleLoadMore}
                            loadingState={loadingState}
                            showFilterPanel={true}
                            onFiltersChange={handleFiltersChange}
                            filters={searchFilters}
                            containerWidth="100%"
                            containerMaxWidth="100%"
                            outerWidth="100%"
                            outerMarginTop={0}
                            transparentContainer
                        />
                    </div>
                );

                const analysisColumn = (
                    <div
                        className={`commanderDetailsColumn${mobile ? '' : ' panelGradient'}`}
                        style={
                            mobile
                                ? { display: 'initial', width: '100%', minWidth: 0 }
                                : {
                                      display: 'initial',
                                      flex: '3 1 600px',
                                      width: 600,
                                      minWidth: 320,
                                      minHeight: window.innerHeight - 70,
                                  }
                        }
                    >
                        <CommanderCardStatsPanel />
                    </div>
                );

                if (mobile) {
                    return (
                        <div className="commanderDetailsMobileTabs panelGradient">
                            <DeepLinkTabs defaultSelectedKey="analysis" isEmphasized UNSAFE_style={{ padding: 0 }}>
                                <TabList marginStart="10px">
                                    <Item key="analysis">
                                        <Data size="XS" />
                                        Analysis
                                    </Item>
                                    <Item key="decks">
                                        <ViewList size="XS" />
                                        Deck Lists
                                    </Item>
                                </TabList>
                                <TabPanels marginTop="10px">
                                    <Item key="analysis">{analysisColumn}</Item>
                                    <Item key="decks">{deckListColumn}</Item>
                                </TabPanels>
                            </DeepLinkTabs>
                        </div>
                    );
                }

                return (
                    <Flex
                        direction="row"
                        justifyContent="space-between"
                        columnGap={20}
                        rowGap={30}
                        marginTop={20}
                        alignSelf="center"
                        alignItems="start"
                        wrap
                        maxWidth="calc(100vw - 20px)"
                        width={1400}
                    >
                        {deckListColumn}
                        {analysisColumn}
                    </Flex>
                );
            })()}
        </Flex>
    );
}
